import { useState, useEffect } from 'react';
import { Row, Col, Button } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import { Loader, useToast, useAppContext } from '@front-activerse/common-components';
import { Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

const DayPlanSummary = ({
  canBeCopiedToDate,
  copyDayPlan,
  alreadyCopied,
  protein,
  fat,
  carbohydrates,
  date,
}) => {
  const { t } = useTranslator();
  const [alreadyClicked, setAlreadyClicked] = useState(alreadyCopied);
  const [preload, setPreload] = useState(false);
  const { trackFirebaseEvent } = useOutletContext();
  const { toastError, toastSuccess } = useToast();

  const {
    images: { copyForTomorrow },
  } = useAppContext();

  useEffect(() => {
    if (alreadyCopied !== alreadyClicked) {
      setAlreadyClicked(alreadyCopied);
      setPreload(false);
    }
  }, [alreadyCopied]);

  const copyDay = async () => {
    if (!alreadyClicked) {
      setAlreadyClicked(true);
      setPreload(true);

      const response = await copyDayPlan.copy();
      if (response.data.me.dietSetCopy.__typename === 'BasicMutationSuccess') {
        setPreload(false);
        toastSuccess(
          <p className="mb-0">{t('meals.notification.copiedMenuConfirmation.content')}</p>
        );
      } else {
        toastError();
        throw new Error(`Copy day plan to next day failed: ${response.me.dietSetCopy.__typename}`);
      }
    }
  };

  return (
    <>
      <Row>
        <Col xs="12" sm={{ size: 8, offset: 2 }} xl={{ size: 6, offset: 3 }}>
          <section className="summary">
            <div className="macro">
              <h3 className="title-small semi-bold">{t('mealPlan.info.distribution.title')}</h3>
              <Row className="divider2">
                <Col xs="4">
                  <h4 className="title-medium semi-bold primary text-text-main-color mb-0">
                    {protein}%
                  </h4>
                  <p className="description text-text-variant-color mb-0">
                    {t('mealPlan.info.distribution.proteins')}
                  </p>
                </Col>
                <Col xs="4">
                  <h4 className="title-medium semi-bold text-text-main-color mb-0">{fat}%</h4>
                  <p className="description text-text-variant-color mb-0">
                    {t('mealPlan.info.distribution.fats')}
                  </p>
                </Col>
                <Col xs="4">
                  <h4 className="title-medium semi-bold text-text-main-color mb-0">
                    {carbohydrates}%
                  </h4>
                  <p className="description text-text-variant-color mb-0">
                    {t('mealPlan.info.distribution.carbs')}
                  </p>
                </Col>
              </Row>
            </div>

            <Row>
              <Col xs={{ size: 10, offset: 1 }}>
                {canBeCopiedToDate && (
                  <Button
                    color="secondary"
                    size="sm"
                    outline
                    disabled={alreadyClicked}
                    className="mb-s w-100"
                    onClick={() => {
                      copyDay();
                      trackFirebaseEvent('user_action', {
                        action: 'tap-copy_dayplan',
                        location: 'Dietplan',
                      });
                    }}
                  >
                    {alreadyClicked ? (
                      <>
                        <img
                          src={copyForTomorrow}
                          alt=""
                          style={{ marginTop: '-5px', marginBottom: '-5px' }}
                          className="me-1"
                        />
                        {t('mealPlan.copiedForTomorrowButton')}
                      </>
                    ) : (
                      t('mealPlan.copyForTomorrowButton')
                    )}
                  </Button>
                )}

                <Button
                  color="secondary"
                  size="sm"
                  outline
                  className="w-100"
                  tag={Link}
                  to={`/meals/day-plan/${date}/change`}
                  onClick={() => {
                    localStorage.setItem('activeTimestamp', 2);
                    trackFirebaseEvent('user_action', {
                      action: 'tap-change_dayplan',
                      location: 'Dietplan',
                    });
                  }}
                >
                  {t('mealPlan.changeDayButton')}
                </Button>
              </Col>
            </Row>
          </section>
        </Col>
      </Row>

      {preload ? <Loader /> : ''}
    </>
  );
};

export default DayPlanSummary;
